import { useEffect, useRef, useState } from "react";
import { agentsOnSelectedLinks } from "./resultsSummaryData";
import { fetchFirstCsv } from "./api/resultsSummaryFetch";
import { fetchSelectedLinksTimeline, fetchSelectedLinksTravelTimeTimeline } from "./api/builderResultsApi";

export default function useSelectedLinkSummaryData({ builderId, runId, prefixes, selectedTime, selectedIds, maxTime, loadHistory, loadAgents, loadTravelTime, isVisible }) {
  const prefixKey = JSON.stringify(prefixes);
  const selectionKey = JSON.stringify([...selectedIds].map(String).sort());
  const historyKey = `${builderId}:${runId}:${selectionKey}`;
  const agentKey = `${runId}:${prefixKey}:${selectionKey}:${selectedTime}`;
  const travelTimeKey = `${builderId}:${runId}:${selectionKey}:${maxTime}`;
  const historyCacheRef = useRef({ key: "", rows: null });
  const travelTimeCacheRef = useRef({ key: "", rows: null });
  const agentCacheRef = useRef(new Map());
  const [historyState, setHistoryState] = useState({ key: "", rows: [], loading: false, error: "" });
  const [travelTimeState, setTravelTimeState] = useState({ key: "", rows: [], loading: false, error: "" });
  const [agentState, setAgentState] = useState({ key: "", rows: [], loading: false, error: "" });

  useEffect(() => {
    if (!runId || !isVisible || !loadHistory || !selectedIds.length) return undefined;
    if (historyCacheRef.current.key !== historyKey) historyCacheRef.current = { key: historyKey, rows: null };
    const controller = new AbortController();
    if (historyCacheRef.current.rows) {
      setHistoryState({ key: historyKey, rows: historyCacheRef.current.rows, loading: false, error: "" });
      return () => controller.abort();
    }
    setHistoryState({ key: historyKey, rows: [], loading: true, error: "" });
    fetchSelectedLinksTimeline(builderId, runId, selectedIds, controller.signal)
      .then((data) => {
        if (controller.signal.aborted) return;
        const rows = data.points.map((point) => ({
          time: Number(point.timeSeconds),
          queued: point.queued == null ? null : Number(point.queued),
          running: point.running == null ? null : Number(point.running),
        })).filter((point) => Number.isFinite(point.time)).sort((a, b) => a.time - b.time);
        historyCacheRef.current.rows = rows;
        setHistoryState({ key: historyKey, rows, loading: false, error: "", unknownLinkIds: data.unknownLinkIds || [] });
      })
      .catch((error) => {
        if (!controller.signal.aborted) setHistoryState({ key: historyKey, rows: [], loading: false, error: String(error?.message || error) });
      });
    return () => controller.abort();
  }, [builderId, historyKey, isVisible, loadHistory, runId, selectionKey, selectedIds]);

  useEffect(() => {
    if (!builderId || !runId || !isVisible || !loadTravelTime || !selectedIds.length || !Number.isFinite(maxTime)) return undefined;
    const controller = new AbortController();
    if (travelTimeCacheRef.current.key === travelTimeKey && travelTimeCacheRef.current.rows) {
      setTravelTimeState({ key: travelTimeKey, rows: travelTimeCacheRef.current.rows, loading: false, error: "" });
      return () => controller.abort();
    }
    setTravelTimeState({ key: travelTimeKey, rows: [], loading: true, error: "" });
    fetchSelectedLinksTravelTimeTimeline(builderId, runId, selectedIds, maxTime, controller.signal)
      .then((data) => {
        if (controller.signal.aborted) return;
        const rows = data.points.map((point) => ({
          time: Number(point.time),
          avg: point.avgTravelTime == null ? null : Number(point.avgTravelTime),
          min: point.minTravelTime == null ? null : Number(point.minTravelTime),
          max: point.maxTravelTime == null ? null : Number(point.maxTravelTime),
        })).filter((point) => Number.isFinite(point.time)).sort((a, b) => a.time - b.time);
        travelTimeCacheRef.current = { key: travelTimeKey, rows };
        setTravelTimeState({ key: travelTimeKey, rows, loading: false, error: "" });
      })
      .catch((error) => {
        if (!controller.signal.aborted) setTravelTimeState({ key: travelTimeKey, rows: [], loading: false, error: String(error?.message || error) });
      });
    return () => controller.abort();
  }, [builderId, isVisible, loadTravelTime, maxTime, runId, selectedIds, travelTimeKey]);

  useEffect(() => {
    if (!runId || !isVisible || !loadAgents || !selectedIds.length) return undefined;
    const controller = new AbortController();
    const cached = agentCacheRef.current.get(agentKey);
    if (cached) {
      setAgentState({ key: agentKey, rows: cached, loading: false, error: "" });
      return () => controller.abort();
    }
    setAgentState({ key: agentKey, rows: [], loading: true, error: "" });
    fetchFirstCsv(prefixes.map((prefix) => `${prefix}/agent_stats/abase-tester_at_${selectedTime}.csv`), controller.signal)
      .then((rows) => {
        if (controller.signal.aborted) return;
        const matching = agentsOnSelectedLinks(rows, selectedIds);
        agentCacheRef.current.set(agentKey, matching);
        if (agentCacheRef.current.size > 8) agentCacheRef.current.delete(agentCacheRef.current.keys().next().value);
        setAgentState({ key: agentKey, rows: matching, loading: false, error: "" });
      })
      .catch(() => {
        if (!controller.signal.aborted) setAgentState({ key: agentKey, rows: [], loading: false, error: "Agent data is unavailable at this time." });
      });
    return () => controller.abort();
  }, [agentKey, isVisible, loadAgents, prefixKey, runId, selectionKey, selectedTime]); // eslint-disable-line react-hooks/exhaustive-deps

  const history = historyState.key === historyKey ? historyState : { rows: [], loading: !!runId, error: "" };
  const travelTime = travelTimeState.key === travelTimeKey ? travelTimeState : { rows: [], loading: !!runId, error: "" };
  const agents = agentState.key === agentKey ? agentState : { rows: [], loading: !!runId, error: "" };
  return { history, historyRows: history.rows, travelTime, travelTimeRows: travelTime.rows, agents };
}
